<template>
  <div class="xfield form-field">
    <slot name="label">
      <label for="nationalIdNumber" class="cuscd-form-field-label"
        >{{ schema.label ? schema.label : "&nbsp;"
        }}<span
          class="required"
          v-if="schema.label && schema.required && schema.showRequiredIndicator"
          >*</span
        ></label
      >
    </slot>
    <div class="input-wrapper">
      <input
        v-bind="$attrs"
        ref="inputRef"
        type="text"
        name="nationalIdNumber"
        id="nationalIdNumber"
        pattern="[0-9\-X]+"
        inputmode="numeric"
        autocomplete="off"
        :disabled="disabled"
        @keyup="handleKeyUp"
        @keydown="validateSsnInput"
        :value="displayValue"
        @focus="isFocused = true"
        @blur="handleBlur"
        @input="handleInput($event)"
        class="text-input"
        :class="[
          'form-control',
          {
            'is-invalid': schema.touched && !schema.valid,
          },
        ]"
      />
      <button
        v-if="showNationalIdNumberToggle"
        type="button"
        @click="toggleNationalIdNumber"
        class="eye-btn"
        aria-label="Toggle SSN visibility"
      >
        <font-awesome-icon class="toggle" :icon="['fas', toggleIcon]" />
      </button>
    </div>
    <div class="inline-checkbox-container" v-if="showItinIndicator" :disabled="disabled">
      <input
        type="checkbox"
        id="itinIndicator"
        v-model="itinIndicator"
        aria-label="Toggle ITIN checkbox"
        @change="$emit('update:itinIndicator', ($event.target as HTMLInputElement).checked)"
      />
      <!-- The 'for' attribute links the label to the input ID for accessibility -->
      <label for="itinIndicator">Indicates ITIN</label>
    </div>
    <ul
      v-if="errors && errors.length >= 1 && !disabled"
      class="invalid-feedback"
      :class="{
        multiple: errors && errors.length > 1,
      }"
    >
      <li v-for="(error, index) in errors" :key="index">
        {{ error }}
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, toRef, onMounted } from "vue";
import * as yup from "yup";
import { useField } from "vee-validate";
import useValidation from "@/use/validation";
import type { FieldSchema } from "@/models/form";
import {
  NationalIdNumberTypeIds,
  NationalIdNumberTypeDefinitions,
} from "@/models/nationalIdNumberTypes";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

const props = withDefaults(
  defineProps<{
    nationalIdNumberTypeId: number;
    modelValue?: string;
    disabled: boolean;
    rules?: yup.Schema;
    schema: FieldSchema;
    errors?: string[] | undefined[];
    showItinIndicator?: boolean;
    useStars?: boolean;
  }>(),
  {
    showItinIndicator: false,
    useStars: false,
  },
);

const modelValue = toRef(props, "modelValue");

const emit = defineEmits<{
  (e: "update:modelValue", value: string | undefined): void;
  (e: "update:itinIndicator", value: boolean): void;
}>();

const { isNationalIdNumberMasked } = useValidation();

const isMasked = ref<boolean>(true);
const isFocused = ref<boolean>(false);
const showNationalIdNumberToggle = ref<boolean>(false);
const itinIndicator = ref<boolean>(false);

const maskedPart = computed<string>(() => {
  return props.useStars ? "***-**-" : "XXX-XX-";
});

const toggleIcon = computed<string>(() => (isMasked.value ? "eye" : "eye-slash"));

const displayValue = computed<string | undefined | unknown>(() => {
  const cleanedValue = props.modelValue?.replace(/[^0-9]/g, "");

  if (props.disabled) {
    if (cleanedValue) {
      if (cleanedValue.length === 4) {
        return maskedPart.value + cleanedValue;
      }

      if (cleanedValue.length === 9) {
        const visiblePart = cleanedValue.slice(-4);
        return maskedPart.value + visiblePart;
      }
    }
  }

  if (isNationalIdNumberMasked(props.modelValue || "") || !isFocused.value) {
    return props.modelValue;
  }

  return value.value;
});

const rawValue = computed<string | undefined>(() => {
  const cleanedValue = props.modelValue?.replace(/[^0-9]/g, "");
  return cleanedValue ? cleanedValue : undefined;
});
const maskedValue = computed<string>(() => {
  const cleanedValue = props.modelValue?.replace(/[^0-9]/g, "");
  return cleanedValue ? `XXX-XX-${cleanedValue.slice(-4)}` : "";
});

const nationalIdNumberLabel = computed<string>(() => {
  return props.nationalIdNumberTypeId == NationalIdNumberTypeIds.ITIN
    ? NationalIdNumberTypeDefinitions[NationalIdNumberTypeIds.ITIN]?.name
    : NationalIdNumberTypeDefinitions[NationalIdNumberTypeIds.SSN]?.name;
});

const { handleChange, value, errors, validate, resetField, setValue } = useField(
  props.schema.name || "nationalIdNumber",
  props.rules || yup.string(),
);

function formatNationalIdNumber(nationalIdNumber: string): string | undefined {
  if (isNationalIdNumberMasked(nationalIdNumber)) {
    return nationalIdNumber;
  }
  // Automatically format the SSN as the user types
  if (nationalIdNumber && !isNationalIdNumberMasked(nationalIdNumber)) {
    // Remove non-numeric characters
    let digits = nationalIdNumber.replace(/\D/g, "");

    // Limit to 9 digits (standard SSN length)
    if (digits.length > 9) {
      digits = digits.slice(0, 9);
    }

    let formattedValue = digits;
    // Apply SSN format: XXX-XX-XXXX
    if (digits.length > 5) {
      formattedValue = `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    } else if (digits.length > 3) {
      formattedValue = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
      formattedValue = digits;
    }

    return formattedValue;
  }

  return undefined;
}

function formatInput(event: Event) {
  const value = (event.target as HTMLInputElement).value;
  const cleanValue = `${value}`.replace(/[^0-9]/g, "");

  if (!value) {
    setValue(undefined);
    return;
  }

  setValue(formatNationalIdNumber(cleanValue));
}

function handleBlur() {
  isFocused.value = false;
  validate();
}

function handleKeyUp(event: Event) {
  formatInput(event);
}

function toggleNationalIdNumber() {
  //#TODO call endpoint to retrieve the unmasked one
  isMasked.value = !isMasked.value;
}

const ignoreKeys: string[] = [
  "Backspace",
  "ArrowLeft",
  "ArrowRight",
  "Tab",
  "Enter",
  "Delete",
  "Shift",
  "CapsLock",
  "Home",
  "End",
];

function validateSsnInput(event: any) {
  // 1. Allow structural, control, and navigation keys (Backspace, Delete, Tab, Escape, Enter, Arrow keys)
  const isControlKey = ignoreKeys.indexOf(event.key) >= 0;

  // 2. Allow keyboard shortcuts (Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X)
  const isShortcut =
    (event.ctrlKey || event.metaKey) && ["a", "c", "v", "x"].includes(event.key.toLowerCase());

  if (isControlKey || isShortcut) {
    return;
  }
  // 3. Block the key if it is not a digit (0-9) or a dash
  const isNumberOrHyphen = /^[0-9-]$/.test(event.key);
  const validDigit = /^[0-9]$/.test(event.key) || /^[-]$/.test(event.key);

  // 4. Do not allow more than 9 numbers
  const cleanedValue = event.target.value?.replace(/[^0-9]/g, "");

  if (!isNumberOrHyphen || cleanedValue.length >= 9) {
    event.preventDefault();
  }
}

function handleInput(event: Event) {
  // Handle edge cases where input is autofill or pasted
  const element = event.target as HTMLInputElement;

  // Global replacement of everything that isn't a number or a hyphen
  element.value = element.value.replace(/[^0-9]/g, "");
  const value = element?.value;

  // format it if there are 9 digits
  if (value && value.length === 9) {
    handleChange(formatNationalIdNumber(value));
  } else {
    handleChange(value);
  }
}

watch(modelValue, (newValue, oldValue) => {
  if (
    (oldValue === "" || oldValue === null || oldValue === undefined) &&
    (newValue === "" || newValue === null || newValue === undefined)
  ) {
    setValue(null, false);
    return;
  }
  if (newValue === "" || newValue === null || newValue === undefined) {
    setValue(null);
  } else {
    setValue(value.value, false);
  }
});

watch(value, () => {
  if (value.value && typeof value.value === "string") {
    emit("update:modelValue", formatNationalIdNumber(value.value));
  } else {
    emit("update:modelValue", undefined);
  }
});

onMounted(() => {
  if (props.modelValue) {
    setValue(value.value, false);
  }
});
defineExpose({
  value,
  errors,
  formatNationalIdNumber,
  maskedValue,
  rawValue,
  isNationalIdNumberMasked,
});
</script>

<style scoped src="@/styles/baseformfield.scss" lang="scss"></style>
<style scoped>
.input-wrapper {
  position: relative;
  width: 100%;
}
.input-wrapper input {
  width: 100%;
  padding: 10px 40px 10px 12px;
  box-sizing: border-box;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.eye-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
}

.eye-btn:hover {
  color: #333;
}

.inline-checkbox-container {
  display: flex;
}

.inline-checkbox-container label {
  padding-bottom: 0;
  font-weight: 400;
  font-size: 12px;
  padding-left: 2px;
  margin-bottom: 0;
}
</style>
